// SmartVideo - Universelles Video-Management für alle Geräte
// Verhindert GPU-Crashes durch intelligentes Laden/Entladen
// Nutzt MP4 mit Hardware-Decode auf allen Geräten

import React, { useEffect, useRef } from 'react';
import { getPreloadTier } from '../utils/preloadGate';

// Global: Nur ein Video gleichzeitig (verhindert paralleles Decoding)
let currentPlayingRef = null;

const saveData = () => !!navigator.connection?.saveData;
const slowNet = () => /^(slow-2g|2g|3g)$/.test(navigator.connection?.effectiveType || '');
const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Entscheidet ob Video erlaubt ist basierend auf:
 * - Netzwerk (Save-Data, langsame Verbindung)
 * - User-Präferenz (prefers-reduced-motion)
 *
 * Hinweis: Wir erlauben Videos auf ALLEN Geräten (auch low-tier),
 * weil MP4 Hardware-Decode sehr effizient ist (~100MB GPU RAM).
 * IntersectionObserver sorgt dafür, dass nur sichtbare Videos laden.
 */
function deviceAllowsVideo() {
  // User will keine Animationen
  if (prefersReducedMotion()) return false;

  // Save-Data Mode aktiv
  if (saveData()) return false;

  // Langsames Netzwerk (2g/3g) - lade keine Videos
  if (slowNet()) return false;

  // MP4 mit Hardware-Decode ist effizient genug für alle Geräte
  return true;
}

/**
 * SmartVideo Component
 * - Lädt Video nur wenn sichtbar (IntersectionObserver)
 * - Gibt Decoder frei wenn nicht sichtbar (wichtig für GPU-RAM!)
 * - Respektiert Geräte-Limits und User-Präferenzen
 * - Nur ein Video gleichzeitig
 * - Nutzt MP4 für universelle Hardware-Decode-Unterstützung
 */
export default function SmartVideo({
  src,
  className = '',
  poster,
  loop = true,
  muted = true,
  playsInline = true,
  style = {},
  autoPlay = true,
  children,
  ...props
}) {
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !src) return;

    const allow = deviceAllowsVideo();

    // Gerät erlaubt kein Video → nur Poster zeigen
    if (!allow) {
      v.pause();
      v.load();
      return;
    }

    let destroyed = false;

    // Fügt Video-Quelle ein
    const attachSources = () => {
      if (destroyed || v.dataset.bound === '1') return;

      v.dataset.bound = '1'; // Sofort markieren um Race Conditions zu vermeiden
      v.innerHTML = `<source src="${src}" type="video/mp4">`;

      // Warte auf Metadaten vor dem Abspielen
      v.addEventListener('loadedmetadata', playSafely, { once: true });
      v.load();
    };

    // Entfernt Video-Quellen (gibt GPU-Decoder frei!)
    const detachSources = () => {
      v.pause();
      v.removeAttribute('src');
      while (v.firstChild) v.removeChild(v.firstChild);
      delete v.dataset.bound;
      v.load(); // Wichtig: Decoder/Buffer freigeben
    };

    // Spielt Video ab (nur eines gleichzeitig)
    const playSafely = async () => {
      if (!autoPlay) return;

      // Stoppe anderes laufendes Video
      if (currentPlayingRef && currentPlayingRef !== v) {
        try {
          currentPlayingRef.pause();
        } catch (e) {
          // Video bereits entfernt
        }
      }

      currentPlayingRef = v;

      try {
        await v.play();
      } catch (e) {
        // Autoplay blockiert oder Video nicht geladen - ok
        if (process.env.NODE_ENV === 'development') {
          console.debug('Video autoplay prevented:', e.message);
        }
      }
    };

    // IntersectionObserver: Lade nur wenn sichtbar
    let hasAttached = false; // Track ob wir schon einmal attached haben
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (destroyed) return;

          if (entry.isIntersecting) {
            // Video ist sichtbar → Quellen laden (nur einmal) und abspielen
            if (!hasAttached) {
              attachSources();
              hasAttached = true;
            } else {
              // Bereits geladen, nur wieder abspielen
              playSafely();
            }
          } else if (hasAttached) {
            // Video ist nicht sichtbar → NUR pausieren, NICHT entladen (verhindert Layout-Shifts)
            v.pause();
            if (currentPlayingRef === v) {
              currentPlayingRef = null;
            }
          }
        }
      },
      {
        threshold: 0.1,        // 10% sichtbar
        rootMargin: '0px'      // Kein Puffer um Layout-Shifts zu vermeiden
      }
    );

    io.observe(v);

    // Visibility API: Pausiere bei Tab-Wechsel, setze bei Rückkehr fort
    let wasPausedByVisibility = false; // Track ob WIR pausiert haben

    const handleVisibilityChange = () => {
      if (document.hidden && currentPlayingRef === v) {
        // Tab-Wechsel WEG: Video pausieren
        try {
          v.pause();
          wasPausedByVisibility = true; // Merken dass WIR pausiert haben
        } catch (e) {
          // Ignore
        }
      } else if (!document.hidden && wasPausedByVisibility && hasAttached) {
        // Tab-Wechsel ZURÜCK: Video fortsetzen (nur wenn WIR es pausiert haben)
        wasPausedByVisibility = false;
        playSafely();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Error Handling
    const handleError = () => {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Video failed to load, showing poster only');
      }
      detachSources();
    };
    v.addEventListener('error', handleError);

    // Cleanup
    return () => {
      destroyed = true;
      io.disconnect();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      v.removeEventListener('error', handleError);

      if (currentPlayingRef === v) {
        currentPlayingRef = null;
      }

      detachSources();
    };
  }, [src, autoPlay]);

  return (
    <video
      ref={videoRef}
      className={className}
      style={style}
      poster={poster}
      preload="metadata"  // NICHT "auto"!
      muted={muted}
      playsInline={playsInline}
      loop={loop}
      aria-label="Decorative background video"
      {...props}
    >
      {children}
    </video>
  );
}
